/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Menu,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { CacheContext } from "../../Context/cacheContext";
import { TaskModel } from "../../Models/Models";
import GenericModal from "../../Modal/GenericModal";
import VehicleSearchModal from "../Generic/VehicleSearchModal";
import SubtaskList from "./SubtaskList";
import {
  TASK_RECURRENCE,
  TASK_RECURRENCE_OPTIONS,
  TASK_STATUS_COLUMN_CONFIG,
} from "../../Utils/tasksConstants";
import { Vocabulary } from "../../Utils/Vocabulary";
import withRole from "../../Utils/withRole";
import { rescanDataFiles } from "../../Utils/FilePreview";

type Props = {
  open: boolean;
  task: TaskModel | null;
  onClose: () => void;
  onSave: (values: TaskModel) => void;
  canAssignTasks?: boolean;
  name?: string;
};

const validationSchema = Yup.object({
  name: Yup.string().required(Vocabulary.required),
  priority: Yup.string().required(Vocabulary.required),
  estimated_duration: Yup.number()
    .nullable()
    .required(Vocabulary.required)
    .min(1, Vocabulary.required),
});

// Muted, desaturated priority palette — soft bg, darker text
const PRIORITY_OPTIONS = [
  {
    value: "low",
    label: Vocabulary.taskPriorityLow,
    bg: "#f0fdf4",
    color: "#15803d",
  },
  {
    value: "medium",
    label: Vocabulary.taskPriorityMedium,
    bg: "#fffbeb",
    color: "#b45309",
  },
  {
    value: "high",
    label: Vocabulary.taskPriorityHigh,
    bg: "#fef2f2",
    color: "#b91c1c",
  },
];

function PropLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        mb: 0.5,
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Renders the task creation and editing modal with vehicle association, document requirement, assignee, status, recurrence, and scheduling inputs.
 *
 * @param props - The task form props.
 * @param props.open - Whether the modal is visible.
 * @param props.task - The task being edited, or null when creating a new task.
 * @param props.onClose - Callback fired when the modal is closed.
 * @param props.onSave - Callback fired when the form is submitted.
 * @param props.canAssignTasks - Whether the current user is allowed to assign tasks to other users.
 * @returns The task form modal UI with supporting dialogs and controls.
 */
function TaskForm({ open, task, onClose, onSave, canAssignTasks }: Props) {
  const cacheContext = useContext(CacheContext);
  const users: any[] = cacheContext.cache?.users ?? [];
  const vehicles: any[] = cacheContext.cache?.vehicule ?? [];
  const currentUserId = Number(localStorage.getItem("useId"));
  const isOwner = !task?.id || task?.created_by_user_id === currentUserId;
  const appTheme = useTheme();
  const isMobileView = useMediaQuery(appTheme.breakpoints.down("sm"));

  // Local theme override — scoped only to this component's subtree
  const modalTheme = useMemo(
    () =>
      createTheme(appTheme, {
        components: {
          MuiDialog: {
            styleOverrides: {
              paper: {
                boxShadow: "none",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
              },
            },
          },
          MuiButton: {
            defaultProps: { disableRipple: true },
            styleOverrides: {
              root: { borderRadius: 6, textTransform: "none" },
            },
          },
          MuiIconButton: {
            defaultProps: { disableRipple: true },
          },
          MuiToggleButton: {
            defaultProps: { disableRipple: true },
          },
          MuiChip: {
            styleOverrides: { root: { borderRadius: 6 } },
          },
          MuiOutlinedInput: {
            styleOverrides: { root: { borderRadius: 6 } },
          },
          MuiFilledInput: {
            styleOverrides: { root: { borderRadius: 6 } },
          },
          MuiMenuItem: {
            defaultProps: { disableRipple: true },
          },
        },
      }),
    [appTheme],
  );

  const isEditing = !!task?.id;

  const taskFormValues = useMemo<TaskModel>(() => {
    if (task?.id) return task;
    return {
      ...new TaskModel(),
      ...task,
      assigned_to_user_id: currentUserId,
    };
  }, [task, currentUserId]);

  const [openVehicleSearch, setOpenVehicleSearch] = useState(false);
  const [vehicle, setVehicle] = useState<{ name: string; vin: string } | null>(
    null,
  );
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TaskModel>({
    resolver: yupResolver(validationSchema) as any,
  });

  /**
   * Resets the form values whenever the `taskFormValues` change, and updates the associated vehicle information if applicable.
   */
  useEffect(() => {
    reset(taskFormValues);
    if (task?.vehicle_id) {
      const cached = vehicles.find((v) => v.id === task.vehicle_id);
      setVehicle({
        name: cached
          ? `${cached.marca ?? ""} ${cached.model ?? ""}`.trim()
          : (task.vehicle_info?.name ?? ""),
        vin: cached?.numar_inmatriculare ?? task.vehicle_info?.vin ?? "",
      });
    } else {
      setVehicle(null);
    }
  }, [taskFormValues, reset]);

  /*
   * When the modal is opened and the task has an associated document, trigger a rescan of the data files after a short delay.
   * This ensures that the document preview is up-to-date and available for viewing.
   */
  useEffect(() => {
    if (open && task?.document_path) {
      setTimeout(rescanDataFiles, 100);
    }
  }, [open, task?.document_path]);

  const recurrence = watch("recurrence");
  const status = watch("status");
  const estimatedDuration = watch("estimated_duration") as number | null;

  const statusConfig = TASK_STATUS_COLUMN_CONFIG.find(
    (s) => s.status === status,
  );

  const handleSelectVehicle = (selected: any) => {
    setValue("vehicle_id", selected.id);
    setVehicle({
      name: `${selected.brand_r?.nume ?? ""} ${selected.model_r?.nume ?? ""}`.trim(),
      vin: selected.vin ?? "",
    });
    setOpenVehicleSearch(false);
  };

  const buttons = (
    <Box sx={{ display: "flex", gap: 1.5, px: 1 }}>
      <Button onClick={onClose} color="inherit" disableRipple>
        {Vocabulary.cancel}
      </Button>
      <Button
        onClick={handleSubmit(onSave)}
        variant="contained"
        color="primary"
        disableElevation
        disableRipple
        sx={{ color: "#fff" }}
      >
        {isEditing ? Vocabulary.save : Vocabulary.taskNew}
      </Button>
    </Box>
  );

  return (
    <ThemeProvider theme={modalTheme}>
      <>
        <GenericModal
          open={open}
          onClose={onClose}
          title={isEditing ? Vocabulary.taskEdit : Vocabulary.taskNew}
          maxWidth={860}
          buttons={buttons}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobileView ? "column" : "row",
                minHeight: isMobileView ? "unset" : 400,
              }}
            >
              {/* ── Left: content ── */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  pr: isMobileView ? 0 : 2.5,
                  display: "flex",
                  flexDirection: "column",
                  pt: 0.25,
                }}
              >
                {/* Title */}
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="standard"
                      placeholder={`${Vocabulary.taskName} *`}
                      fullWidth
                      multiline
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        mb: 1,
                        "& .MuiInput-root": {
                          fontSize: 19,
                          fontWeight: 700,
                          color: "#0f172a",
                          lineHeight: 1.3,
                        },
                        "& textarea::placeholder": {
                          color: "#cbd5e1",
                          opacity: 1,
                          fontWeight: 600,
                          fontSize: 19,
                        },
                      }}
                    />
                  )}
                />

                {/* Description */}
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="standard"
                      placeholder={`${Vocabulary.taskDescription}…`}
                      fullWidth
                      multiline
                      minRows={2}
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        mb: 2,
                        "& .MuiInput-root": {
                          fontSize: 13.5,
                          color: "#475569",
                          lineHeight: 1.7,
                        },
                        "& textarea::placeholder": {
                          color: "#c8d0db",
                          opacity: 1,
                        },
                      }}
                    />
                  )}
                />

                {/* Meta cards */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                    mb: 2.5,
                  }}
                >
                  {/* Vehicle card */}
                  <Box
                    onClick={() => setOpenVehicleSearch(true)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      p: 1.25,
                      borderRadius: "10px",
                      cursor: "pointer",
                      border: "1px solid #e2e8f0",
                      bgcolor: "#fafafa",
                      transition: "all 0.12s",
                      "&:hover": { borderColor: "#FF6633", bgcolor: "#FFF5F2" },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
                    >
                      <DirectionsCarIcon
                        sx={{
                          fontSize: 13,
                          color: vehicle ? "#FF6633" : "#94a3b8",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 600,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {Vocabulary.associateWithVehicle}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: vehicle ? "#111827" : "#c8d0db",
                      }}
                    >
                      {vehicle?.name || Vocabulary.taskNotSet}
                    </Typography>
                    {vehicle?.vin && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontFamily: "ui-monospace, monospace",
                          color: "#9ca3af",
                          letterSpacing: "0.03em",
                        }}
                      >
                        {vehicle.vin}
                      </Typography>
                    )}
                  </Box>

                  {/* Document card */}
                  <Controller
                    name="requires_document"
                    control={control}
                    render={({ field }) => (
                      <Box
                        onClick={() => field.onChange(!field.value)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          p: 1.25,
                          borderRadius: "10px",
                          cursor: "pointer",
                          border: "1px solid",
                          transition: "all 0.12s",
                          borderColor: field.value ? "#FFAA99" : "#e2e8f0",
                          bgcolor: field.value ? "#FFF5F2" : "#fafafa",
                          "&:hover": {
                            borderColor: "#FF6633",
                            bgcolor: "#FFF5F2",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.6,
                            }}
                          >
                            <AttachFileIcon
                              sx={{
                                fontSize: 13,
                                color: field.value ? "#FF6633" : "#94a3b8",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 10.5,
                                fontWeight: 600,
                                color: "#94a3b8",
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                              }}
                            >
                              {Vocabulary.taskDocLabel}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 28,
                              height: 15,
                              borderRadius: "10px",
                              transition: "background 0.15s",
                              bgcolor: field.value ? "#FF6633" : "#e2e8f0",
                              position: "relative",
                            }}
                          >
                            <Box
                              sx={{
                                width: 11,
                                height: 11,
                                borderRadius: "50%",
                                bgcolor: "#fff",
                                position: "absolute",
                                top: 2,
                                left: field.value ? 15 : 2,
                                transition: "left 0.15s",
                              }}
                            />
                          </Box>
                        </Box>
                        {isEditing && task?.document_path ? (
                          <Box
                            data-file={`/api/${task.document_path}`}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                              mt: 0.25,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#FF6633",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              {Vocabulary.taskDocUploaded}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: field.value ? "#FF6633" : "#c8d0db",
                            }}
                          >
                            {field.value
                              ? Vocabulary.taskCompletionCondition
                              : Vocabulary.taskNotSet}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                </Box>

                {/* Subtasks */}
                {isEditing && task?.id && (
                  <SubtaskList
                    taskId={task.id}
                    initialSubtasks={task.subtasks ?? []}
                  />
                )}
              </Box>

              {/* ── Right: sidebar ── */}
              <Box
                sx={{
                  width: isMobileView ? "100%" : 256,
                  minWidth: 0,
                  overflow: "hidden",
                  bgcolor: "#fafafa",
                  borderLeft: isMobileView ? "none" : "1px solid #f0f0f0",
                  borderTop: isMobileView ? "1px solid #f0f0f0" : "none",
                  px: 2.5,
                  py: 2,
                  mt: isMobileView ? 1.5 : 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Responsabil — visible only with assignTasks permission */}
                {canAssignTasks && (
                  <Box>
                    <PropLabel>{Vocabulary.taskResponsible}</PropLabel>
                    <Controller
                      name="assigned_to_user_id"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          size="small"
                          options={users}
                          getOptionLabel={(u: any) => u.nume ?? u.name ?? ""}
                          value={
                            users.find((u) => u.id === field.value) ?? null
                          }
                          onChange={(_, v: any) =>
                            field.onChange(v?.id ?? null)
                          }
                          renderOption={(props: any, option: any) => (
                            <MenuItem {...props} key={option.id} disableRipple>
                              <Avatar
                                sx={{
                                  width: 20,
                                  height: 20,
                                  fontSize: 10,
                                  mr: 1,
                                  bgcolor: "#FF6633",
                                }}
                              >
                                {(option.nume ??
                                  option.name ??
                                  "?")[0]?.toUpperCase()}
                              </Avatar>
                              <Typography sx={{ fontSize: 13 }}>
                                {option.nume ?? option.name}
                              </Typography>
                            </MenuItem>
                          )}
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              placeholder={Vocabulary.taskResponsible}
                              variant="outlined"
                              size="small"
                            />
                          )}
                        />
                      )}
                    />
                  </Box>
                )}

                {/* Prioritate */}
                <Box>
                  <PropLabel>{Vocabulary.taskPriority}</PropLabel>
                  <Controller
                    name="priority"
                    control={control}
                    render={({ field }) => (
                      <>
                        <ToggleButtonGroup
                          exclusive
                          fullWidth
                          size="small"
                          value={field.value ?? null}
                          onChange={(_, v) => field.onChange(v)}
                          sx={
                            errors.priority
                              ? {
                                  "& .MuiToggleButton-root": {
                                    borderColor: "#d32f2f !important",
                                  },
                                }
                              : {}
                          }
                        >
                          {PRIORITY_OPTIONS.map((p) => (
                            <ToggleButton
                              key={p.value}
                              value={p.value}
                              disableRipple
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: "none",
                                py: 0.4,
                                borderRadius: "6px !important",
                                border: "1px solid #e5e7eb !important",
                                color: "#6b7280",
                                "&.Mui-selected": {
                                  bgcolor: p.bg,
                                  color: p.color,
                                  borderColor: "transparent !important",
                                  "&:hover": { bgcolor: p.bg },
                                },
                              }}
                            >
                              {p.label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                        {errors.priority && (
                          <Typography
                            sx={{ fontSize: 11, color: "#d32f2f", mt: 0.5 }}
                          >
                            {errors.priority.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>

                {/* Status */}
                <Box>
                  <PropLabel>{Vocabulary.taskStatus}</PropLabel>
                  <Chip
                    onClick={(e) => setStatusAnchor(e.currentTarget)}
                    label={statusConfig?.label ?? ""}
                    size="small"
                    icon={
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: statusConfig?.color,
                          ml: "8px !important",
                          flexShrink: 0,
                        }}
                      />
                    }
                    deleteIcon={
                      <ChevronRightIcon
                        sx={{ transform: "rotate(90deg)", fontSize: 15 }}
                      />
                    }
                    onDelete={(e) => setStatusAnchor(e.currentTarget)}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      justifyContent: "space-between",
                      bgcolor: "background.paper",
                      fontWeight: 600,
                      fontSize: 12,
                      borderColor: "#e5e7eb",
                    }}
                  />
                  <Menu
                    anchorEl={statusAnchor}
                    open={!!statusAnchor}
                    onClose={() => setStatusAnchor(null)}
                    PaperProps={{
                      sx: {
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "none",
                        mt: 0.5,
                      },
                    }}
                  >
                    {TASK_STATUS_COLUMN_CONFIG.map((s) => (
                      <MenuItem
                        key={s.status}
                        disableRipple
                        onClick={() => {
                          setValue("status", s.status);
                          setStatusAnchor(null);
                        }}
                        selected={status === s.status}
                        sx={{ fontSize: 13, gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: s.color,
                            flexShrink: 0,
                          }}
                        />
                        {s.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>

                {/* Durată estimată */}
                <Box>
                  <PropLabel>{Vocabulary.taskEstimatedDuration}</PropLabel>
                  <TimePicker
                    ampm={false}
                    views={["hours", "minutes"]}
                    format="HH:mm"
                    disabled={!isOwner}
                    value={
                      estimatedDuration != null
                        ? moment()
                            .startOf("day")
                            .add(estimatedDuration, "minutes")
                        : null
                    }
                    onChange={(v) =>
                      setValue(
                        "estimated_duration",
                        v ? v.hours() * 60 + v.minutes() : null,
                        { shouldValidate: true },
                      )
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        placeholder:
                          Vocabulary.taskEstimatedDurationPlaceholder,
                        error: !!errors.estimated_duration,
                        helperText: errors.estimated_duration?.message,
                      },
                    }}
                  />
                </Box>

                {/* Periodicitate */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: recurrence ? 1 : 0,
                    }}
                  >
                    <PropLabel>{Vocabulary.taskPeriodicity}</PropLabel>
                    <Switch
                      size="small"
                      checked={!!recurrence}
                      onChange={(_, on) =>
                        setValue("recurrence", on ? "daily" : null)
                      }
                      color="primary"
                      disableRipple
                      sx={{ mr: -0.5 }}
                    />
                  </Box>
                  {recurrence && (
                    <Controller
                      name="recurrence"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(e.target.value || null)
                          }
                          variant="outlined"
                          SelectProps={{
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  borderRadius: "8px",
                                  border: "1px solid #e5e7eb",
                                  boxShadow: "none",
                                },
                              },
                            },
                          }}
                        >
                          {TASK_RECURRENCE_OPTIONS.map((o) => (
                            <MenuItem
                              key={o.value}
                              value={o.value}
                              disableRipple
                              sx={{ fontSize: 13 }}
                            >
                              {o.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  )}
                  {recurrence === TASK_RECURRENCE.DAILY && (
                    <Controller
                      name="include_weekends"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ mt: 0.5 }}
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={field.onChange}
                              color="primary"
                              size="small"
                              disableRipple
                            />
                          }
                          label={
                            <Typography
                              sx={{ fontSize: 12, color: "text.secondary" }}
                            >
                              {Vocabulary.taskWeekendIncluded}
                            </Typography>
                          }
                        />
                      )}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        </GenericModal>

        <VehicleSearchModal
          open={openVehicleSearch}
          onClose={() => setOpenVehicleSearch(false)}
          onSelectVehicle={handleSelectVehicle}
        />
      </>
    </ThemeProvider>
  );
}

export default withRole(TaskForm);
